import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { 
  GraduationCap, Users, LogOut, UserPlus, BarChart3, Brain, 
  ChevronRight, Key, AlertTriangle, TrendingUp, Copy, CheckCircle, 
  ExternalLink, BookOpen, Plus, Loader2, FileUp, FileText, Trash2, Shield, Calculator, Link
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ParentDashboard({ user, logout, onLoginAsStudent }) {
  const [students, setStudents] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingTestId, setDeletingTestId] = useState(null);
  const [newStudentGrade, setNewStudentGrade] = useState("");
  const [newStudentCode, setNewStudentCode] = useState(null);
  const [newStudentDisplayName, setNewStudentDisplayName] = useState("");
  const [copiedLink, setCopiedLink] = useState(false);
  
  const getDirectLoginLink = (code) => {
    return `${window.location.origin}/student-login?code=${code}`;
  };

  const handleCopyLink = (code) => {
    const link = getDirectLoginLink(code);
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    toast.success("Direct login link copied!");
    setTimeout(() => setCopiedLink(false), 2000);
  };
  const [loggingInAs, setLoggingInAs] = useState(null);
  
  // Metrics & Logs State
  const [drillSessions, setDrillSessions] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Test creation state
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [creatingTest, setCreatingTest] = useState(false);
  const [testName, setTestName] = useState("");
  const [testGrade, setTestGrade] = useState("");
  const [testYear, setTestYear] = useState(new Date().getFullYear().toString());
  const [testSheetFile, setTestSheetFile] = useState(null);
  const [answerKeyFile, setAnswerKeyFile] = useState(null);
  const [answerKeyMode, setAnswerKeyMode] = useState("pdf");
  const [totalQuestions, setTotalQuestions] = useState(24);
  const [answers, setAnswers] = useState({});
  const [concepts, setConcepts] = useState({});
  
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [studentsRes, testsRes] = await Promise.all([
        axios.get(`${API}/parent/students`),
        axios.get(`${API}/tests`).catch(() => ({ data: [] }))
      ]);
      setStudents(studentsRes.data);
      setTests(testsRes.data || []);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDrillMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const response = await axios.get(`${API}/drills/sessions/history`);
      setDrillSessions(response.data.sessions || []);
    } catch (error) {
      console.error("Failed to fetch drill metrics:", error);
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchDrillMetrics();
  }, [fetchData, fetchDrillMetrics]);

  const handleCreateStudent = async () => {
    if (!newStudentGrade) {
      toast.error("Please select a grade level");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/parent/students`, {
        grade_level: newStudentGrade
      });
      
      setNewStudentCode(response.data.access_code);
      setNewStudentDisplayName(response.data.display_name);
      toast.success("Student account created!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create student");
    } finally {
      setCreating(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setNewStudentGrade("");
    setNewStudentCode(null);
    setNewStudentDisplayName("");
    setCopiedCode(false);
    setCopiedLink(false);
  };

  const handleLoginAsStudent = async (student) => {
    if (!student.access_code) {
      toast.error("No access code found for this student");
      return;
    }
    
    setLoggingInAs(student.user_id);
    try {
      const response = await axios.post(`${API}/auth/student-login`, {
        access_code: student.access_code
      });
      
      toast.success(`Logged in as ${student.display_name}`);
      
      if (onLoginAsStudent) {
        onLoginAsStudent(response.data);
      } else {
        window.location.href = '/dashboard';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to login as student");
    } finally {
      setLoggingInAs(null);
    }
  };

  // Test creation handlers
  const handleTestSheetChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error("Please upload a PDF file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File too large. Maximum 5MB allowed.");
        return;
      }
      setTestSheetFile(file);
    }
  };

  const handleAnswerKeyFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error("Please upload a PDF file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File too large. Maximum 5MB allowed.");
        return;
      }
      setAnswerKeyFile(file);
    }
  };

  const handleAnswerChange = (questionNum, value) => {
    setAnswers(prev => ({ ...prev, [questionNum]: value.toUpperCase() }));
  };

  const handleConceptChange = (questionNum, value) => {
    setConcepts(prev => ({ ...prev, [questionNum]: value }));
  };

  const handleCreateTest = async () => {
    if (!testName.trim()) {
      toast.error("Please enter a test name");
      return;
    }
    if (!testGrade) {
      toast.error("Please select a grade level");
      return;
    }
    if (!testSheetFile) {
      toast.error("Please upload the test questions PDF");
      return;
    }
    if (answerKeyMode === "pdf" && !answerKeyFile) {
      toast.error("Please upload the answer key PDF");
      return;
    }
    if (answerKeyMode === "manual") {
      const filledAnswers = Object.keys(answers).filter(k => answers[k]);
      if (filledAnswers.length === 0) {
        toast.error("Please enter at least one answer");
        return;
      }
    }

    setCreatingTest(true);
    try {
      const formData = new FormData();
      formData.append("name", testName.trim());
      formData.append("grade_level", testGrade);
      formData.append("year", testYear);
      formData.append("total_questions", totalQuestions.toString());
      formData.append("test_sheet", testSheetFile);
      formData.append("answer_key_mode", answerKeyMode);
      
      if (answerKeyMode === "pdf") {
        formData.append("answer_key_pdf", answerKeyFile);
      } else {
        formData.append("answers_json", JSON.stringify(answers));
        formData.append("concepts_json", JSON.stringify(concepts));
      }

      const response = await axios.post(`${API}/tests`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (answerKeyMode === "pdf") {
        toast.success(`Test created! AI extracted ${response.data.total_questions} answers.`);
      } else {
        toast.success("Test created successfully!");
      }
      setTestDialogOpen(false);
      resetTestForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create test");
    } finally {
      setCreatingTest(false);
    }
  };

  const handleDeleteTest = async (testId) => {
    setDeletingTestId(testId);
    try {
      const response = await axios.delete(`${API}/tests/${testId}`);
      toast.success(`Test deleted. ${response.data.files_deleted} files removed.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete test");
    } finally {
      setDeletingTestId(null);
    }
  };

  const resetTestForm = () => {
    setTestName("");
    setTestGrade("");
    setTestYear(new Date().getFullYear().toString());
    setTestSheetFile(null);
    setAnswerKeyFile(null);
    setAnswerKeyMode("pdf");
    setTotalQuestions(24);
    setAnswers({});
    setConcepts({});
  };

  const conceptOptions = [
    "Algebra", "Geometry", "Logic", "Number Theory", 
    "Combinatorics", "Arithmetic", "Word Problems", "Patterns"
  ];

  if (loading) {
    return (
      <div className="min-h-screen graph-paper-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen graph-paper-bg">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                MathTrack
              </h1>
              <p className="text-sm text-gray-500">Parent Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-medium text-gray-900">{user?.display_name || "Parent"}</p>
              <p className="text-sm text-gray-500">{user?.role}</p>
            </div>
            {user?.is_admin && (
              <Button 
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin")}
                className="gap-2"
              >
                <Shield className="w-4 h-4" />
                Admin
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs for Children and Tests */}
        <Tabs defaultValue="children" className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="children" data-testid="children-tab">
              <Users className="w-4 h-4 mr-2" />
              Your Children
            </TabsTrigger>
            <TabsTrigger value="drills" data-testid="drills-tab">
              <Calculator className="w-4 h-4 mr-2" />
              Math Drills Config
            </TabsTrigger>
            <TabsTrigger value="metrics" data-testid="metrics-tab">
              <BarChart3 className="w-4 h-4 mr-2" />
              Progress & Logs
            </TabsTrigger>
            <TabsTrigger value="tests" data-testid="tests-tab">
              <BookOpen className="w-4 h-4 mr-2" />
              Tests & Answer Keys
            </TabsTrigger>
          </TabsList>

          {/* Children Tab */}
          <TabsContent value="children">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Your Children
                </h2>
                <p className="text-gray-500">Create accounts and monitor their learning progress</p>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-primary hover:bg-primary/90"
                    data-testid="add-student-btn"
                  >
                    <UserPlus className="w-5 h-5 mr-2" />
                    Add Student
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {newStudentCode ? "Student Account Created!" : "Add New Student"}
                    </DialogTitle>
                  </DialogHeader>
                  
                  {newStudentCode ? (
                    <div className="space-y-4 pt-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                        <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                        <p className="text-green-800 font-medium mb-4">
                          Account created for {newStudentDisplayName}!
                        </p>
                        <div className="bg-white border-2 border-green-300 rounded-lg p-4 mb-4">
                          <p className="text-sm text-gray-500 mb-2">Login Code:</p>
                          <p className="text-4xl font-mono font-bold text-green-700 tracking-[0.3em]">
                            {newStudentCode}
                          </p>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <Button
                            variant="outline"
                            onClick={() => {
                              navigator.clipboard.writeText(newStudentCode);
                              setCopiedCode(true);
                              toast.success("Code copied!");
                            }}
                          >
                            {copiedCode ? (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Code Copied
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4 mr-2" />
                                Copy Code
                              </>
                            )}
                          </Button>
                          
                          <Button
                            variant="outline"
                            onClick={() => handleCopyLink(newStudentCode)}
                          >
                            {copiedLink ? (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Link Copied
                              </>
                            ) : (
                              <>
                                <Link className="w-4 h-4 mr-2" />
                                Copy Link
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 text-center">
                        Share this code with your child to let them log in.
                      </p>
                      <Button onClick={handleCloseDialog} className="w-full">
                        Done
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4 pt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Grade Level
                        </label>
                        <Select value={newStudentGrade} onValueChange={setNewStudentGrade}>
                          <SelectTrigger data-testid="grade-select">
                            <SelectValue placeholder="Select grade" />
                          </SelectTrigger>
                          <SelectContent>
                            {["Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", 
                              "Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10",
                              "Grade 11", "Grade 12"].map(grade => (
                              <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-sm text-gray-500">
                        A unique display name will be automatically generated for your child.
                      </p>
                      <Button
                        onClick={handleCreateStudent}
                        disabled={creating}
                        className="w-full bg-primary hover:bg-primary/90"
                        data-testid="create-student-btn"
                      >
                        {creating ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            Creating...
                          </>
                        ) : (
                          "Create Student Account"
                        )}
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>

            {/* Students Grid */}
            {students.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Students Yet</h3>
                  <p className="text-gray-500 mb-4">Add your first child to get started</p>
                  <Button onClick={() => setDialogOpen(true)}>
                    <UserPlus className="w-5 h-5 mr-2" />
                    Add Student
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {students.map((student, index) => (
                  <motion.div
                    key={student.user_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="overflow-hidden" data-testid={`student-card-${student.user_id}`}>
                      {/* Student Header */}
                      <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                            <GraduationCap className="w-6 h-6" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold">{student.display_name || "Student"}</h3>
                            <p className="text-green-100">{student.grade_level || "Grade not set"}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Access Code Section */}
                      {student.access_code && (
                        <div className="bg-blue-50 border-b border-blue-200 p-4">
                          <div className="flex items-center justify-between flex-wrap gap-3">
                            <div className="flex items-center gap-3">
                              <Key className="w-5 h-5 text-blue-600" />
                              <span className="text-sm font-medium text-blue-800">Login Code:</span>
                              <span className="font-mono font-bold text-xl text-blue-900 tracking-[0.2em] bg-white px-3 py-1 rounded-lg border-2 border-blue-300">
                                {student.access_code}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  navigator.clipboard.writeText(student.access_code);
                                  toast.success("Code copied!");
                                }}
                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCopyLink(student.access_code)}
                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                                title="Copy Direct Login Link"
                              >
                                <Link className="w-4 h-4" />
                              </Button>
                            </div>
                            <Button
                              onClick={() => handleLoginAsStudent(student)}
                              disabled={loggingInAs === student.user_id}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              {loggingInAs === student.user_id ? (
                                "Logging in..."
                              ) : (
                                <>
                                  <ExternalLink className="w-4 h-4 mr-2" />
                                  Login as Student
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Stats */}
                      <CardContent className="p-6">
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <BarChart3 className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-gray-900 font-mono">
                              {student.stats?.submissions || 0}
                            </p>
                            <p className="text-sm text-gray-500">Tests Taken</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <TrendingUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-gray-900 font-mono">
                              {student.stats?.average || 0}%
                            </p>
                            <p className="text-sm text-gray-500">Average Score</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                            <p className="text-lg font-bold text-gray-900">
                              {student.stats?.top_weakness || "None"}
                            </p>
                            <p className="text-sm text-gray-500">Focus Area</p>
                          </div>
                        </div>
                        
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={() => navigate(`/student/${student.user_id}/progress`)}
                        >
                          View Detailed Progress
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Math Drills Tab */}
          <TabsContent value="drills">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <Calculator className="w-5 h-5 inline-block mr-2 text-indigo-600" />
                    Math Multiplication Drills
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Configure timed multiplication practice for your children
                  </p>
                </div>
                <Button 
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => navigate("/drills")}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Manage Drills
                </Button>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <Calculator className="w-16 h-16 text-indigo-200 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Practice Makes Perfect
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto mb-4">
                    Create custom multiplication drills for your children. Choose which times tables 
                    to practice (1-25), and track their progress as they improve from 10 seconds 
                    per question down to 3 seconds!
                  </p>
                  <div className="flex flex-wrap justify-center gap-4 mb-6">
                    <div className="bg-indigo-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-indigo-600">10s → 3s</p>
                      <p className="text-sm text-indigo-600">Adaptive Speed</p>
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-indigo-600">Smart</p>
                      <p className="text-sm text-indigo-600">Question Selection</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Metrics & Logs Tab */}
          <TabsContent value="metrics">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Progress & Logs
                </h2>
                <p className="text-gray-500">Track student performance on multiplication drills</p>
              </div>
              <Button variant="outline" onClick={fetchDrillMetrics} disabled={metricsLoading}>
                {metricsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh Logs"}
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Recent Drill Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                {drillSessions.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No drill sessions recorded yet.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">Date</th>
                          <th className="text-left p-2">Student</th>
                          <th className="text-left p-2">Drill Name</th>
                          <th className="text-left p-2">Score</th>
                          <th className="text-left p-2">Accuracy</th>
                          <th className="text-left p-2">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {drillSessions.map((session, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-2">
                              {new Date(session.completed_at).toLocaleString()}
                            </td>
                            <td className="p-2 font-medium">
                              {session.student_display_name}
                            </td>
                            <td className="p-2">{session.drill_name}</td>
                            <td className="p-2">
                              {session.correct_count}/{session.total_questions}
                            </td>
                            <td className="p-2">
                              <Badge 
                                className={
                                  session.accuracy >= 90 ? "bg-green-100 text-green-700 hover:bg-green-100" :
                                  session.accuracy >= 70 ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-100" :
                                  "bg-red-100 text-red-700 hover:bg-red-100"
                                }
                              >
                                {session.accuracy}%
                              </Badge>
                            </td>
                            <td className="p-2">{session.total_time_seconds}s</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tests Tab */}
          <TabsContent value="tests">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Tests & Answer Keys
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Upload test papers and answer keys for your children to practice
                  </p>
                </div>
                <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="create-test-btn"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Create Test
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Create New Test
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6 pt-4">
                      {/* Basic Info */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Test Name *
                          </label>
                          <Input
                            placeholder="e.g., Math Kangaroo 2024 Level 5-6"
                            value={testName}
                            onChange={(e) => setTestName(e.target.value)}
                            data-testid="test-name-input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Grade Level *
                          </label>
                          <Select value={testGrade} onValueChange={setTestGrade}>
                            <SelectTrigger data-testid="grade-level-select">
                              <SelectValue placeholder="Select grade" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Pre-Ecolier">Pre-Ecolier (1-2)</SelectItem>
                              <SelectItem value="Ecolier">Ecolier (3-4)</SelectItem>
                              <SelectItem value="Benjamin">Benjamin (5-6)</SelectItem>
                              <SelectItem value="Cadet">Cadet (7-8)</SelectItem>
                              <SelectItem value="Junior">Junior (9-10)</SelectItem>
                              <SelectItem value="Student">Student (11-12)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Year
                          </label>
                          <Input
                            type="number"
                            value={testYear}
                            onChange={(e) => setTestYear(e.target.value)}
                            data-testid="year-input"
                          />
                        </div>
                      </div>

                      {/* Test Questions PDF Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <FileUp className="w-4 h-4 inline mr-1" />
                          Test Questions (PDF) *
                        </label>
                        <label className={`dropzone ${testSheetFile ? 'border-green-500 bg-green-50' : ''}`}>
                          <input
                            type="file"
                            accept="application/pdf"
                            onChange={handleTestSheetChange}
                            className="hidden"
                            data-testid="test-sheet-input"
                          />
                          {testSheetFile ? (
                            <div className="flex items-center gap-3 text-green-600">
                              <CheckCircle className="w-8 h-8" />
                              <div className="text-left">
                                <p className="font-medium">{testSheetFile.name}</p>
                                <p className="text-sm text-gray-500">Click to change</p>
                              </div>
                            </div>
                          ) : (
                            <div className="text-gray-500">
                              <FileUp className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                              <p className="font-medium">Upload test questions PDF</p>
                              <p className="text-sm">Max 5MB</p>
                            </div>
                          )}
                        </label>
                      </div>

                      {/* Answer Key Mode Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          <Key className="w-4 h-4 inline mr-1" />
                          Answer Key *
                        </label>
                        <RadioGroup 
                          value={answerKeyMode} 
                          onValueChange={setAnswerKeyMode}
                          className="flex flex-col gap-2"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="pdf" id="answer-pdf" />
                            <Label htmlFor="answer-pdf" className="cursor-pointer">
                              Upload Answer Key PDF (AI extracts answers automatically)
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="manual" id="answer-manual" />
                            <Label htmlFor="answer-manual" className="cursor-pointer">
                              Enter answers manually
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {/* Answer Key PDF Upload */}
                      {answerKeyMode === "pdf" && (
                        <div>
                          <label className={`dropzone ${answerKeyFile ? 'border-green-500 bg-green-50' : ''}`}>
                            <input
                              type="file"
                              accept="application/pdf"
                              onChange={handleAnswerKeyFileChange}
                              className="hidden"
                              data-testid="answer-key-input"
                            />
                            {answerKeyFile ? (
                              <div className="flex items-center gap-3 text-green-600">
                                <CheckCircle className="w-8 h-8" />
                                <div className="text-left">
                                  <p className="font-medium">{answerKeyFile.name}</p>
                                  <p className="text-sm text-gray-500">Click to change</p>
                                </div>
                              </div>
                            ) : (
                              <div className="text-gray-500">
                                <Key className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                                <p className="font-medium">Upload answer key PDF</p>
                                <p className="text-sm">AI will extract all correct answers</p>
                              </div>
                            )}
                          </label>
                        </div>
                      )}

                      {/* Manual Answer Entry */}
                      {answerKeyMode === "manual" && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Total Questions
                            </label>
                            <Input
                              type="number"
                              min="1"
                              max="100"
                              value={totalQuestions}
                              onChange={(e) => setTotalQuestions(parseInt(e.target.value) || 24)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Answer Key (Enter correct answers)
                            </label>
                            <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                              <div className="grid grid-cols-4 gap-3">
                                {Array.from({ length: totalQuestions }, (_, i) => i + 1).map((num) => (
                                  <div key={num} className="flex items-center gap-2">
                                    <span className="text-sm font-mono text-gray-600 w-6">{num}.</span>
                                    <Input
                                      className="w-14 text-center font-mono uppercase"
                                      maxLength={1}
                                      placeholder="?"
                                      value={answers[num] || ""}
                                      onChange={(e) => handleAnswerChange(num, e.target.value)}
                                    />
                                    <Select 
                                      value={concepts[num] || ""} 
                                      onValueChange={(v) => handleConceptChange(num, v)}
                                    >
                                      <SelectTrigger className="w-28 text-xs">
                                        <SelectValue placeholder="Concept" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {conceptOptions.map(c => (
                                          <SelectItem key={c} value={c}>{c}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      <Button
                        onClick={handleCreateTest}
                        disabled={creatingTest || !testName.trim() || !testGrade || !testSheetFile || (answerKeyMode === "pdf" && !answerKeyFile)}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        {creatingTest ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            {answerKeyMode === "pdf" ? "Processing with AI..." : "Creating Test..."}
                          </>
                        ) : (
                          <>
                            <Plus className="w-5 h-5 mr-2" />
                            Create Test
                          </>
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {tests.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">No tests yet. Create one to get started.</p>
                    <Button onClick={() => setTestDialogOpen(true)}>
                      <Plus className="w-5 h-5 mr-2" />
                      Create Test
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Grade Level</TableHead>
                        <TableHead>Year</TableHead>
                        <TableHead>Questions</TableHead>
                        <TableHead>Files</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="w-20">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tests.map((test) => (
                        <TableRow 
                          key={test.test_id}
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => navigate(`/test/${test.test_id}`)}
                        >
                          <TableCell className="font-medium">
                            <span className="text-purple-600 hover:underline">
                              {test.name}
                            </span>
                          </TableCell>
                          <TableCell>{test.grade_level}</TableCell>
                          <TableCell className="font-mono">{test.year}</TableCell>
                          <TableCell className="font-mono">{test.total_questions}</TableCell>
                          <TableCell>
                            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                              {test.test_sheet_url && (
                                <a 
                                  href={`${BACKEND_URL}${test.test_sheet_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-purple-600 hover:underline flex items-center gap-1 text-sm"
                                >
                                  <FileText className="w-3 h-3" />
                                  PDF
                                </a>
                              )}
                              {test.answer_key_url && (
                                <a 
                                  href={`${BACKEND_URL}${test.answer_key_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-green-600 hover:underline flex items-center gap-1 text-sm"
                                >
                                  <Key className="w-3 h-3" />
                                  PDF
                                </a>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-500">
                            {new Date(test.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  disabled={deletingTestId === test.test_id}
                                >
                                  {deletingTestId === test.test_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Test?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete &quot;{test.name}&quot; including all uploaded PDFs and student submissions. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteTest(test.test_id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
